import { useEffect, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useErroPadrao from '../../../hooks/useErroPadrao';
import useHelp from '../../../hooks/useHelp';
import { CHAVE_TOKEN, real, dataBR, hojeISO } from "../../../utils/formata";

// "2026-08-22" + 1 dia -> "2026-08-23" (1ª parcela da renovação cai no dia seguinte)
function amanhaISO() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function EmprestimoCadastro() {
    const { id } = useParams(); // presente = edição OU renovação (definido pela rota)
    const location = useLocation();
    const modoRenovar = location.pathname.startsWith("/renovar-emprestimo/");
    const modoEditar = !!id && !modoRenovar;
    const [emprestimo, setEmprestimo] = useState({
        metodoJuros: "fixo",
        periodicidade: "diaria_seg_sab",
        status: "ativo",
        dataContrato: hojeISO(),
        reaproveitarAssinatura: true,
    });
    const [empresaFixa, setEmpresaFixa] = useState(null); // na renovação a empresa é travada
    const [empresas, setEmpresas] = useState([]);
    const [cronograma, setCronograma] = useState(null);
    const [simulando, setSimulando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const { consultarEmpresas, simularEmprestimo, adicionarEmprestimo, consultarEmprestimo, editarEmprestimo, renovarEmprestimo } = useHelp();
    const { setErroPadrao } = useErroPadrao();
    const navigate = useNavigate();

    useEffect(() => {
        if (!localStorage.getItem(CHAVE_TOKEN)) {
            navigate("/login");
            return;
        }
        consultarEmpresas()
            .then((data) => setEmpresas(data || []))
            .catch((error) => setErroPadrao(error));

        if (id) {
            consultarEmprestimo(id)
                .then((e) => {
                    if (modoRenovar) {
                        // Renovação: reaproveita empresa e condições, mas com datas novas
                        setEmpresaFixa(e.Empresa || null);
                        setEmprestimo({
                            EmpresaId: e.EmpresaId,
                            valorPrincipal: Number(e.valorPrincipal),
                            taxaJuros: Number(e.taxaJuros),
                            numParcelas: e.numParcelas,
                            metodoJuros: e.metodoJuros,
                            periodicidade: e.periodicidade || "diaria_seg_sab",
                            status: "ativo",
                            dataContrato: hojeISO(),
                            dataPrimeiroVencimento: amanhaISO(),
                            obs: `Renovação do contrato #${e.id}`,
                            reaproveitarAssinatura: !!e.assinaturaBase64,
                        });
                        return;
                    }
                    const pendentes = (e.Parcelas || []).filter((p) => p.status !== "paga").sort((a, b) => a.numero - b.numero);
                    setEmprestimo({
                        EmpresaId: e.EmpresaId,
                        valorPrincipal: Number(e.valorPrincipal),
                        taxaJuros: Number(e.taxaJuros),
                        numParcelas: e.numParcelas,
                        metodoJuros: e.metodoJuros,
                        periodicidade: e.periodicidade || "diaria_seg_sab",
                        status: e.status,
                        dataContrato: e.dataContrato,
                        dataPrimeiroVencimento: pendentes[0]?.dataVencimento || e.dataPrimeiroVencimento,
                        obs: e.obs || "",
                    });
                })
                .catch((error) => setErroPadrao(error));
        }
    }, [consultarEmpresas, consultarEmprestimo, id, modoRenovar, setErroPadrao, navigate]);

    function handleChange(e) {
        const { name, value } = e.target;
        // Cliente pessoa física entra com cobrança mensal por padrão (empresa é diária)
        if (name === "EmpresaId") {
            const cli = empresas.find((x) => String(x.id) === String(value));
            const extra = cli?.tipoPessoa === "fisica" ? { periodicidade: "mensal" } : {};
            setEmprestimo({ ...emprestimo, EmpresaId: value, ...extra });
            setCronograma(null);
            return;
        }
        setEmprestimo({ ...emprestimo, [name]: value });
        setCronograma(null);
    }

    const podeSimular = emprestimo.valorPrincipal && emprestimo.taxaJuros && emprestimo.numParcelas && emprestimo.dataPrimeiroVencimento;

    function handleSimular() {
        setSimulando(true);
        simularEmprestimo(emprestimo)
            .then((c) => setCronograma(c))
            .catch((error) => setErroPadrao(error))
            .finally(() => setSimulando(false));
    }

    function handleSubmit(e) {
        e.preventDefault();
        if (!cronograma) {
            handleSimular();
            return;
        }
        setSalvando(true);
        const acao = modoRenovar
            ? renovarEmprestimo(id, emprestimo)
            : modoEditar
                ? editarEmprestimo(id, emprestimo)
                : adicionarEmprestimo(emprestimo);
        acao
            .catch((error) => setErroPadrao(error))
            .finally(() => setSalvando(false));
    }

    return (
        <div className="pagina">
            <h2 className="he-titulo">{modoRenovar ? "Renovar" : modoEditar ? "Editar" : "Novo"} <span className="he-dourado">{modoRenovar ? `contrato (base #${id})` : `Empréstimo ${modoEditar ? `#${id}` : ""}`}</span></h2>
            {modoEditar && <p style={{color: "#9aa5b5", fontSize: "0.85em"}}>⚠️ Parcelas já pagas são preservadas — as condições novas redistribuem o restante. O "1º vencimento" vale pra próxima parcela em aberto.</p>}
            {modoRenovar && <p style={{color: "#9aa5b5", fontSize: "0.85em"}}>♻️ Novo contrato pra <strong className="he-dourado">{empresaFixa?.nomeFantasia || empresaFixa?.razaoSocial || "o mesmo cliente"}</strong> — condições já vieram do contrato anterior. Ajuste valor e parcelas e confirme.</p>}

            <form onSubmit={handleSubmit} className="form-mobile" style={{display: "flex", gap: "1em", flexWrap: "wrap", alignItems: "flex-start", marginTop: "1em"}}>
                <div className="he-card" style={{flex: "1 1 420px", minWidth: 0}}>
                    <h5 className="he-titulo he-dourado" style={{marginBottom: "1em"}}><i className="bi bi-sliders"></i> Condições</h5>

                    <div className="campo-form">
                        <label className="form-label he-dourado">Empresa *</label>
                        <select className="form-select" name="EmpresaId" value={emprestimo.EmpresaId || ""} onChange={handleChange} required disabled={modoRenovar}>
                            <option value="" hidden>Selecionar empresa</option>
                            {empresas.map((e) => <option key={e.id} value={e.id}>{e.tipoPessoa === "fisica" ? "👤 " : ""}{e.nomeFantasia || e.razaoSocial} — {e.cnpj}</option>)}
                        </select>
                        {modoRenovar && <small style={{color: "#9aa5b5"}}>Cliente da renovação — travado no contrato de origem.</small>}
                    </div>

                    <div className="row">
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">Valor emprestado (R$) *</label>
                            <input className="form-control" type="number" step="0.01" min="1" name="valorPrincipal" value={emprestimo.valorPrincipal || ""} onChange={handleChange} required/>
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">{emprestimo.metodoJuros === "fixo" ? "Juros do contrato (% total) *" : "Taxa de juros (% ao mês) *"}</label>
                            <input className="form-control" type="number" step="0.01" min="0" name="taxaJuros" value={emprestimo.taxaJuros || ""} onChange={handleChange} required/>
                            {emprestimo.metodoJuros === "fixo" && <small style={{color: "#9aa5b5"}}>Ex: 20 = empresta 30.000, volta 36.000</small>}
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">Pagamento</label>
                            <select className="form-select" name="periodicidade" value={emprestimo.periodicidade} onChange={handleChange}>
                                <option value="diaria">Diário — todos os dias</option>
                                <option value="diaria_seg_sab">Diário — seg a sáb</option>
                                <option value="diaria_seg_sex">Diário — seg a sex</option>
                                <option value="semanal">Semanal — a cada 7 dias</option>
                                <option value="quinzenal">Quinzenal — a cada 15 dias</option>
                                <option value="mensal">Mensal</option>
                            </select>
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">{emprestimo.periodicidade === "mensal" ? "Nº de parcelas (meses) *" : emprestimo.periodicidade === "semanal" ? "Nº de parcelas (semanas) *" : emprestimo.periodicidade === "quinzenal" ? "Nº de parcelas (quinzenas) *" : "Nº de parcelas (dias) *"}</label>
                            <input className="form-control" type="number" min="1" max="365" name="numParcelas" value={emprestimo.numParcelas || ""} onChange={handleChange} required/>
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">Método de juros</label>
                            <select className="form-select" name="metodoJuros" value={emprestimo.metodoJuros} onChange={handleChange}>
                                <option value="fixo">Fixo no contrato (% total)</option>
                                <option value="simples">Juros simples (% ao mês)</option>
                                <option value="price">Price / composto (% ao mês)</option>
                            </select>
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">Data do contrato *</label>
                            <input className="form-control" type="date" name="dataContrato" value={emprestimo.dataContrato || ""} onChange={handleChange} required/>
                        </div>
                        <div className="col-md-6 campo-form">
                            <label className="form-label he-dourado">1º vencimento *</label>
                            <input className="form-control" type="date" name="dataPrimeiroVencimento" value={emprestimo.dataPrimeiroVencimento || ""} onChange={handleChange} required/>
                        </div>
                        {(!modoEditar) &&
                        <div className="col-12 campo-form">
                            <label className="form-label he-dourado">Situação inicial</label>
                            <select className="form-select" name="status" value={emprestimo.status} onChange={handleChange}>
                                <option value="ativo">Liberado agora (gera saída no caixa)</option>
                                <option value="analise">Em análise (liberar depois)</option>
                            </select>
                        </div>}
                        <div className="col-12 campo-form">
                            <label className="form-label he-dourado">Observações</label>
                            <textarea className="form-control" name="obs" rows={2} value={emprestimo.obs || ""} onChange={handleChange}></textarea>
                        </div>
                        {modoRenovar &&
                        <div className="col-12 campo-form">
                            <label style={{display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "#e8eef5", fontSize: "0.9em"}}>
                                <input type="checkbox" checked={!!emprestimo.reaproveitarAssinatura} onChange={(e) => setEmprestimo({...emprestimo, reaproveitarAssinatura: e.target.checked})}/>
                                Reaproveitar a assinatura do contrato anterior
                            </label>
                            <small style={{color: "#9aa5b5"}}>Desmarque se quiser colher uma assinatura nova pra este contrato.</small>
                        </div>}
                    </div>
                </div>

                <div className="he-card sem-rolagem" style={{flex: "1 1 380px", minWidth: 0}}>
                    <h5 className="he-titulo he-dourado" style={{marginBottom: "1em"}}><i className="bi bi-calculator"></i> Simulação</h5>

                    {!cronograma &&
                        <button type="button" className="btn btn-he" style={{width: "100%"}} disabled={!podeSimular || simulando} onClick={handleSimular}>
                            {simulando ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-magic"></i> Simular parcelas</>}
                        </button>
                    }

                    {cronograma && <>
                        <div className="cards-estatistica" style={{marginBottom: "1em"}}>
                            <div className="card-estat largura-total">
                                <div className="estat-rotulo">TOTAL A RECEBER</div>
                                <div className="estat-numero">{real(cronograma.valorTotal)}</div>
                            </div>
                            <div className="card-estat">
                                <div className="estat-rotulo">PARCELA</div>
                                <div className="estat-numero" style={{color: "#f3f4f6"}}>{cronograma.parcelas.length}x {real(cronograma.valorParcela)}</div>
                            </div>
                            <div className="card-estat">
                                <div className="estat-rotulo">LUCRO</div>
                                <div className="estat-numero" style={{color: "#2ecc71"}}>{real(cronograma.valorTotal - Number(emprestimo.valorPrincipal))}</div>
                            </div>
                        </div>

                        <div className="lista-simulacao">
                            <table className="table table-striped tabela-responsiva tabela-mini">
                                <thead><tr><th>Nº</th><th>Vencimento</th><th>Valor</th></tr></thead>
                                <tbody>
                                    {cronograma.parcelas.map((p) => (
                                        <tr key={p.numero}>
                                            <td className="principal" data-rotulo="Parcela">{p.numero}</td>
                                            <td data-rotulo="Vencimento">{dataBR(p.dataVencimento)}</td>
                                            <td className="destaque" data-rotulo="Valor">{real(p.valor)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <button type="submit" className="btn btn-he" style={{width: "100%", marginTop: "0.8em"}} disabled={salvando}>
                            {salvando ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-check-lg"></i> {modoRenovar ? "Confirmar renovação" : "Confirmar e gravar empréstimo"}</>}
                        </button>
                    </>}
                </div>
            </form>
        </div>
    )
}
